/** Drag handles between the editor's panels (library | stage | inspector, and the timeline).
 * Sizes live in CSS variables on .editor and are remembered per browser; double-click resets. */

import { useEffect, useState } from "react";

type Sizes = { left: number; right: number; bottom: number };
const KEY = "studio:layout";
const DEFAULTS: Sizes = { left: 250, right: 290, bottom: 0 }; // bottom 0 = the stylesheet's default height
const MIN = { left: 160, right: 220, bottom: 120 };

function stored(): Sizes {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? "{}") };
  } catch {
    return DEFAULTS;
  }
}

/** Applies the saved sizes to the editor element and renders the three handles. */
export function Splitters({ editor }: { editor: React.RefObject<HTMLDivElement | null> }) {
  const [sizes, setSizes] = useState(stored);

  useEffect(() => {
    const el = editor.current;
    if (!el) return;
    el.style.setProperty("--left-w", `${sizes.left}px`);
    el.style.setProperty("--right-w", `${sizes.right}px`);
    if (sizes.bottom) el.style.setProperty("--bottom-h", `${sizes.bottom}px`);
    else el.style.removeProperty("--bottom-h");
    try {
      localStorage.setItem(KEY, JSON.stringify(sizes));
    } catch {}
  }, [sizes, editor]);

  const drag = (which: keyof Sizes) => (e: React.PointerEvent) => {
    const el = editor.current;
    if (!el) return;
    e.preventDefault();
    const box = el.getBoundingClientRect();
    const bottomNow = (el.querySelector(".bottom") as HTMLElement | null)?.getBoundingClientRect().height ?? 240;
    document.body.classList.add(which === "bottom" ? "resizing-v" : "resizing-h");
    const move = (ev: PointerEvent) => {
      setSizes((s) => {
        // leave the stage at least 320 px wide and 160 px tall
        if (which === "left") return { ...s, left: Math.round(Math.min(Math.max(MIN.left, ev.clientX - box.left), box.width - s.right - 320)) };
        if (which === "right") return { ...s, right: Math.round(Math.min(Math.max(MIN.right, box.right - ev.clientX), box.width - s.left - 320)) };
        return { ...s, bottom: Math.round(Math.min(Math.max(MIN.bottom, box.bottom - ev.clientY), box.height - 46 - 160)) };
      });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.classList.remove("resizing-v", "resizing-h");
    };
    if (which === "bottom" && !sizes.bottom) setSizes((s) => ({ ...s, bottom: Math.round(bottomNow) }));
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const reset = (which: keyof Sizes) => () => setSizes((s) => ({ ...s, [which]: DEFAULTS[which] }));

  return (
    <>
      <div className="split split-left" title="Drag to resize; double-click to reset" onPointerDown={drag("left")} onDoubleClick={reset("left")} />
      <div className="split split-right" title="Drag to resize; double-click to reset" onPointerDown={drag("right")} onDoubleClick={reset("right")} />
      <div className="split split-bottom" title="Drag to resize; double-click to reset" onPointerDown={drag("bottom")} onDoubleClick={reset("bottom")} />
    </>
  );
}
